import { createReducer, on } from '@ngrx/store';
import { VehicleWithImagesResponseDto, VehicleQueryParams } from '../../services/vehicle.service';
import * as GarageActions from '../actions/garage.actions';

export interface GarageState {
  vehicles: VehicleWithImagesResponseDto[] | null;
  vehicleDetailsMap: { [vehicleId: string]: VehicleWithImagesResponseDto };
  // Pagination state
  currentPage: number;
  pageSize: number;
  totalPages: number;
  sort: 'make-model-year' | 'firstName' | 'lastName' | 'email' | 'vin';
  sortDir: 'asc' | 'desc';
  loading: boolean;
  error: any;
}

export const initialState: GarageState = {
  vehicles: null,
  vehicleDetailsMap: {},
  currentPage: 1,
  pageSize: 10,
  totalPages: 0,
  sort: 'make-model-year',
  sortDir: 'asc',
  loading: false,
  error: null
};

export const garageReducer = createReducer(
  initialState,

  // Load Vehicles
  on(GarageActions.loadVehicles, (state) => ({
    ...state,
    loading: true,
    error: null
  })),

  on(GarageActions.loadVehiclesSuccess, (state, { vehicles, currentPage, pageSize, totalPages }) => ({
    ...state,
    vehicles,
    currentPage,
    pageSize,
    totalPages,
    loading: false,
    error: null
  })),

  on(GarageActions.loadVehiclesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Clear Vehicles
  on(GarageActions.clearVehicles, (state) => ({
    ...state,
    vehicles: null,
    vehicleDetailsMap: {}, // Clear vehicle details map to prevent data leakage
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    sort: 'make-model-year',
    sortDir: 'asc',
    loading: false,
    error: null
  })),

  // Load Vehicle Detail
  on(GarageActions.loadVehicleDetail, (state) => ({
    ...state,
    loading: true,
    error: null
  })),

  on(GarageActions.loadVehicleDetailSuccess, (state, { vehicleId, vehicle }) => ({
    ...state,
    vehicleDetailsMap: {
      ...state.vehicleDetailsMap,
      [vehicleId]: vehicle
    },
    loading: false,
    error: null
  })),

  on(GarageActions.loadVehicleDetailFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Update Vehicle in List
  on(GarageActions.updateVehicleInList, (state, { vehicle }) => {
    // If vehicles array doesn't exist yet, return state unchanged
    if (!state.vehicles) {
      return state;
    }

    // Find the existing vehicle in the list
    const existingVehicle = state.vehicles.find(v => v.VehicleId === vehicle.VehicleId);
    if (!existingVehicle) {
      return state; // Vehicle not in list, don't add it
    }

    // Check if form data has changed
    const formDataChanged =
      existingVehicle.Year !== vehicle.Year ||
      existingVehicle.Make !== vehicle.Make ||
      existingVehicle.Model !== vehicle.Model ||
      existingVehicle.Vin !== vehicle.Vin;

    // Get primary images
    const existingPrimaryImage = existingVehicle.VehicleImages.find(img => img.IsPrimary);
    const newPrimaryImage = vehicle.VehicleImages.find(img => img.IsPrimary);

    // Check if primary image has changed
    const primaryImageChanged = existingPrimaryImage?.ImageId !== newPrimaryImage?.ImageId;

    // Only update if something actually changed
    if (!formDataChanged && !primaryImageChanged) {
      return state;
    }

    // Create updated vehicle with only the primary image
    const vehicleForList: VehicleWithImagesResponseDto = {
      ...vehicle,
      VehicleImages: newPrimaryImage ? [newPrimaryImage] : []
    };

    // Update the vehicle in the array
    const updatedVehicles = state.vehicles.map(v =>
      v.VehicleId === vehicle.VehicleId ? vehicleForList : v
    );

    return {
      ...state,
      vehicles: updatedVehicles,
      // Also update the vehicleDetailsMap to keep it in sync (with all images)
      vehicleDetailsMap: {
        ...state.vehicleDetailsMap,
        [vehicle.VehicleId]: vehicle
      }
    };
  })
);
